// =============================================================================
// Site Types for Sites Page
// Data source: PostgreSQL via GET /v4/sites API
// =============================================================================

/**
 * Site data from the V4 API (PostgreSQL)
 */
export interface Site {
  id: string; // UUID
  name: string;
  siteCode?: string;
  abbreviations?: string[];
  tags?: string[];
  disabled?: boolean;
  status?: 'active' | 'inactive';

  // Contact info
  phone?: string;
  phoneExtension?: string;
  fax?: string;
  email?: string;

  // Address fields (V4 naming)
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  stateProvince?: string;
  postalCode?: string;
  country?: string;
  latitude?: number;
  longitude?: number;

  // Location names (from LEFT JOIN to locations table)
  locationNames?: string[];

  // Account info (from JOIN)
  accountId: string;
  accountName?: string;
  accountNumber?: string;

  // MongoDB references (for backward compatibility during migration)
  mongoSiteId?: string;

  // Timestamps
  createdAt?: string;
  updatedAt?: string;
  description?: string;

  // Legacy fields for backward compatibility with V3 responses
  // These are used only during transition period when editing
  _id?: string;
  organisation?: string | PopulatedOrganisation;
  address?: string;
  postcode?: string;
  state_province_county?: string;
  extension?: string;
  fax_extension?: string;
  geolocation?: [number, number];
  locations?: string[] | PopulatedLocation[];
  usergroups?: string[] | PopulatedUsergroup[];
  created_at?: string;
}

/**
 * Populated organisation data (V3 legacy)
 */
export interface PopulatedOrganisation {
  _id: string;
  name: string;
}

/**
 * Populated usergroup data (V3 legacy)
 */
export interface PopulatedUsergroup {
  _id: string;
  name: string;
}

/**
 * Populated location data (V3 legacy)
 */
export interface PopulatedLocation {
  _id: string;
  name: string;
}

/**
 * Filters for querying sites
 */
export interface SitesFilters {
  search?: string;
  accountId?: string;
  status?: 'active' | 'inactive';
  tags?: string[];
  disabled?: boolean;
  sortKey?: 'name' | 'city' | 'status' | 'createdAt' | 'accountName';
  sortDirection?: 1 | -1;
  pageNumber?: number;
  pageLength?: number;
  // Per-column filters
  name?: string;
  address?: string;
  locations?: string;
}

/**
 * API response for GET /v4/sites
 */
export interface V4SitesResponse {
  sites: Site[];
  pageNumber: number;
  pageLength: number;
  total: number;
}

/**
 * Paginated response wrapper
 */
export interface SitesResponse {
  sites: Site[];
  pageNumber: number;
  pageLength: number;
  total: number;
}

/**
 * Account for dropdown filter (V4)
 */
export interface Account {
  id: string;
  name: string;
  accountNumber?: string;
}

/**
 * Organisation for dropdown filter (V3 legacy - used during transition)
 */
export interface Organisation {
  _id: string;
  name: string;
  org_num?: number;
  enabled?: boolean;
}

/**
 * Location option for dropdown (V3 legacy)
 */
export interface LocationOption {
  _id: string;
  name: string;
}

/**
 * Usergroup option for dropdown (V3 legacy)
 */
export interface UsergroupOption {
  _id: string;
  name: string;
}

/**
 * Options response for new/edit site modal
 */
export interface SiteOptions {
  locations: LocationOption[];
  usergroups: UsergroupOption[];
}

/**
 * Input for creating a new site (V4)
 */
export interface CreateSiteInput {
  accountId: string; // V4 uses accountId
  name: string;
  abbreviations?: string[];
  tags?: string[];
  phone?: string;
  phoneExtension?: string;
  fax?: string;
  email?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  stateProvince?: string;
  postalCode?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
  description?: string;
}

/**
 * Input for updating a site (V4)
 */
export interface UpdateSiteInput {
  name?: string;
  abbreviations?: string[];
  tags?: string[];
  disabled?: boolean;
  phone?: string;
  phoneExtension?: string;
  fax?: string;
  faxExtension?: string;
  email?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  stateProvince?: string;
  postalCode?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
  description?: string;
}

/**
 * Known site tags
 */
export const SITE_TAGS = [
  'destinataire',
  'expéditeur',
  'transporteur',
  'bypass',
  'envois sans échantillons permis',
  'billing',
  'shipping',
] as const;

export type SiteTag = (typeof SITE_TAGS)[number];

/**
 * Admin user for usergroup preview
 */
export interface AdminUser {
  _id: string;
  firstname: string;
  lastname: string;
  email?: string;
}

/**
 * Changelog entry for a site modification
 */
export interface ChangelogEntry {
  _id?: string;
  timestamp: string;
  user: {
    firstname?: string;
    lastname?: string;
  } | null;
  isOriginal?: boolean;
  changes?: Array<{
    field: string;
    old: unknown;
    new: unknown;
  }>;
}

/**
 * Response from GET /v4/sites/:id/changelog
 */
export interface SiteChangelogResponse {
  changelog: ChangelogEntry[];
}
